import { useAuthStore } from '../stores/authStore';
import { useUIStore } from '../stores/uiStore';

const BASE_URL = '/api';

class ApiError extends Error {
  status: number;
  data?: unknown;
  constructor(message: string, status: number, data?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.data = data;
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = useAuthStore.getState().token;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const config: RequestInit = {
    method,
    headers,
  };

  if (body && method !== 'GET') {
    config.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(`${BASE_URL}${path}`, config);

    if (response.status === 401) {
      useAuthStore.getState().logout();
      window.location.href = '/login';
      throw new ApiError('לא מורשה - יש להתחבר מחדש', 401);
    }

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const message = errorData.message || errorData.error || `שגיאה ${response.status}`;
      throw new ApiError(message, response.status);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const json = await response.json();

    // Unwrap the standard { success, data, error } envelope from the backend.
    // When success is true, return just `data` so callers get the payload directly.
    if (json && typeof json === 'object' && 'success' in json) {
      if (json.success === true) {
        return (json.data ?? json) as T;
      }
      // success: false - throw with the error message (keep server-provided data for UI)
      throw new ApiError(json.error || json.message || `שגיאה ${response.status}`, response.status, json.data);
    }

    return json;
  } catch (err) {
    if (err instanceof ApiError) {
      useUIStore.getState().addToast({
        type: 'error',
        message: err.message,
      });
      throw err;
    }

    const message = err instanceof Error ? err.message : 'שגיאת רשת לא צפויה';
    useUIStore.getState().addToast({
      type: 'error',
      message,
    });
    throw new ApiError(message, 0);
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
};
